import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AssetDto, GalleryDto } from '@nusign/picflow-backend-types';
import { Observable, Subject, firstValueFrom, map, take, takeUntil } from 'rxjs';
import { ActivityGroup, ActivityGroupDay, ActivityItem } from '../../../interfaces/activity.interface';
import { sleep } from '../../../lib/sleep';
import { ActivityService } from '../../../services/activity.service';
import { DialogService } from '../../../services/dialog.service';
import { StateService } from '../../../services/state.service';
import { UnseenActivityService } from '../../../services/unseen-activity.service';
import { WatcherService } from '../../../services/watcher.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { ActivityGroupComponent } from '../../components/activity-group/activity-group.component';
import { ActivityAssetsDialogComponent } from '../../dialogs/activity-assets-dialog/activity-assets-dialog.component';
import { ShareGalleryDialogComponent } from '../../dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { ShareSubGalleryDialogComponent } from '../../dialogs/share-sub-gallery-dialog/share-sub-gallery-dialog.component';

@Component({
  selector: 'app-asset-activity-menu',
  templateUrl: './asset-activity-menu.component.html',
  styleUrls: ['./asset-activity-menu.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    SvgComponent,
    ButtonDirective,
    LoadingIndicatorComponent,
    RouterLink,
    ActivityGroupComponent,
    GalleryTeaserUrlPipe,
  ],
})
export class AssetActivityMenuComponent implements OnInit, OnDestroy {
  public activityService = inject(ActivityService);
  public unseenActivityService = inject(UnseenActivityService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  private watcherService = inject(WatcherService);
  private check = inject(ChangeDetectorRef);

  @ViewChild('activity') activityEl: ElementRef<HTMLDivElement>;
  loading = true;
  page = 0;
  days: ActivityGroupDay[] = [];
  bottomReached = false;
  tempNewMap: Map<string, boolean> = new Map();
  markingAllAsSeen = false;
  onOpenNewCount: number;
  receiveNotifications$: Observable<boolean>;
  destroyed$ = new Subject<void>();
  @Input() gallery: GalleryDto;
  @Input() asset: AssetDto;

  @Output() closeContext = new EventEmitter<void>();

  async ngOnInit() {
    this.receiveNotifications$ = this.watcherService.mySubscriptions$.pipe(
      map(watchers => !!watchers.find(w => w.assetId === this.asset.id))
    );
    this.onOpenNewCount = this.state.unseenActivityCountMap.get(this.asset.id);
    this.check.detectChanges();

    this.dialog.dialog$.pipe(takeUntil(this.destroyed$)).subscribe(dialog => {
      if (!dialog) return;
      if (!this.dialog.dialog$.value) return;
      this.closeContext.emit();
    });

    await sleep(0); // avoid changed before checked error
    this.calcInfiniteScroll(true, true);
  }

  public async calcInfiniteScroll(force = false, ignoreLoading = false) {
    if (this.loading && !ignoreLoading) return;
    if (this.bottomReached) return;
    const bounds = this.activityEl.nativeElement.getBoundingClientRect();
    const bottom = bounds.bottom - window.innerHeight;

    if (bottom > 5 && !force) return; // 5 instead of 0 because of rounding errors. Could probably be 1
    this.loading = true;
    const { days, isMore } = await firstValueFrom(
      this.activityService.getAssetActivityWithUserActivity(this.asset.id, this.page)
    );
    this.page += 1;

    if (!isMore) this.bottomReached = true;
    days.forEach(day => {
      const c = this.days.find(d => d.name === day.name);
      if (c) c.activityGroups = c.activityGroups.concat(day.activityGroups);
      else this.days.push(day);
      this.autoMarkAsSeen(day);
    });
    this.days.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
    this.check.detectChanges();
    await sleep(0);
    this.loading = false;
    this.check.detectChanges();
    return this.calcInfiniteScroll();
  }

  /**
   * Automatically marks all activities that are in the current view and
   * are not associated with an asset as seen (views, gallery downloads, and multi asset downloads)
   */
  autoMarkAsSeen(day: ActivityGroupDay) {
    const items: ActivityItem[] = day.activityGroups
      .reduce((all, group) => [...all, ...group.items], [])
      .filter(item => {
        if (!item.id) return false;
        if (this.unseenActivityService.seenMap.get(item.id)) return false;
        return !item.context.assetId;
      });

    items.forEach(item => this.tempNewMap.set(item.id, true));
    this.unseenActivityService.markAsSeen(items);
    this.check.detectChanges();
  }

  async markAllAsSeen() {
    if (this.markingAllAsSeen) return;
    this.markingAllAsSeen = true;
    this.tempNewMap.clear();
    await this.unseenActivityService.markGalleryAsSeen(this.gallery);
    this.onOpenNewCount = 0;
    this.markingAllAsSeen = false;
    this.check.detectChanges();
  }

  public clickGroup(group: ActivityGroup) {
    if (!group.assets.length) return;
    this.dialog.open(ActivityAssetsDialogComponent, { group });
  }

  public isSeen(group: ActivityGroup) {
    const hasAnyUnseen = group.items.find(item => {
      if (!item.id) return false;
      if (this.tempNewMap.get(item.id)) return true;
      return !this.unseenActivityService.seenMap.get(item.id);
    });
    return !hasAnyUnseen;
  }

  toggleNotifications() {
    this.receiveNotifications$.pipe(take(1)).subscribe(subscribed => {
      if (subscribed) this.watcherService.unWatchGallery$.next(this.gallery.id);
      else this.watcherService.watchGallery$.next(this.gallery.id);
    });
  }

  async openShareDialog() {
    if (this.gallery.section) {
      this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery });
    } else {
      this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
